// src/components/admin/MainsSubmissionViewer.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";

// ✅ Use env variable or fallback
const API_BASE =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

const MainsSubmissionViewer = () => {
  const { testId } = useParams();
  const navigate = useNavigate();

  const [submissions, setSubmissions] = useState([]);
  const [testDetails, setTestDetails] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [comment, setComment] = useState("");
  const [marks, setMarks] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const token = JSON.parse(localStorage.getItem("user"))?.token;

  useEffect(() => {
    if (!testId) return;
    fetchTestDetails();
    fetchSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testId]);

  // ✅ Fetch test details
  const fetchTestDetails = async () => {
    try {
      const res = await axios.get(
        `${API_BASE}/api/admin/test-series/mains/${testId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setTestDetails(res.data);
    } catch (err) {
      console.error("Error fetching test details:", err);
      setError("Failed to load test details.");
    }
  };

  // ✅ Fetch all submissions for this test
  const fetchSubmissions = async () => {
    try {
      setError("");
      const res = await axios.get(
        `${API_BASE}/api/admin/test-series/mains/submissions/${testId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSubmissions(res.data);
    } catch (err) {
      console.error("Error fetching submissions:", err);
      setError("Failed to load submissions. Please try again.");
    }
  };

  // ✅ Evaluate a submission
  const handleEvaluate = async () => {
    if (!selectedSubmission) return;
    if (!marks || isNaN(marks)) {
      alert("Please enter valid marks.");
      return;
    }
    setLoading(true);
    setError("");

    try {
      await axios.put(
        `${API_BASE}/api/admin/test-series/mains/evaluate/${selectedSubmission._id}`,
        { comment, marks: Number(marks) },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setComment("");
      setMarks("");
      setSelectedSubmission(null);
      fetchSubmissions();
      alert("Evaluation submitted successfully!");
    } catch (err) {
      console.error("Evaluation failed:", err);
      setError("Failed to submit evaluation. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="mb-4 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-100 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition"
      >
        ← Back
      </button>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 dark:bg-red-800 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-200 rounded">
          {error}
        </div>
      )}

      {/* Test Details */}
      {testDetails && (
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded">
          <h2 className="text-xl font-semibold text-blue-800 dark:text-blue-200 mb-2">
            {testDetails.title}
          </h2>
          <p className="text-sm text-blue-600 dark:text-blue-300 mb-2">
            <strong>Section:</strong> {testDetails.section || "—"} |{" "}
            <strong>Subject:</strong> {testDetails.subject || "—"}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            <strong>Questions:</strong>{" "}
            {testDetails.questions?.length || 0} questions
          </p>
        </div>
      )}

      <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
        📄 Submissions ({submissions.length})
      </h3>

      {submissions.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          No submissions yet for this test.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full table-auto border text-left border-gray-300 dark:border-gray-700">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-800">
                <th className="px-4 py-2">User</th>
                <th className="px-4 py-2">Submitted At</th>
                <th className="px-4 py-2">Submitted File</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Marks</th>
                <th className="px-4 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {submissions.map((submission) => (
                <tr
                  key={submission._id}
                  className="border-t border-gray-300 dark:border-gray-700"
                >
                  <td className="px-4 py-2">
                    {submission.userId?.name || "Unknown"} <br />
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {submission.userId?.email}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    {submission.submittedAt
                      ? new Date(submission.submittedAt).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-4 py-2">
                    {submission.fileUrl ? (
                      <a
                        href={submission.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300"
                      >
                        📎 View File
                      </a>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">
                        No file
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {submission.evaluated ? (
                      <span className="text-green-600 dark:text-green-400 font-medium">
                        ✅ Reviewed
                      </span>
                    ) : (
                      <span className="text-yellow-600 dark:text-yellow-400 font-medium">
                        ⏳ Pending
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {submission.evaluated ? submission.marks || "—" : "—"}
                  </td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => {
                        setSelectedSubmission(submission);
                        setComment(submission.comment || "");
                        setMarks(submission.marks ?? "");
                      }}
                      className={`${
                        submission.evaluated
                          ? "bg-orange-500 dark:bg-orange-600 hover:bg-orange-600 dark:hover:bg-orange-700"
                          : "bg-blue-500 dark:bg-blue-600 hover:bg-blue-600 dark:hover:bg-blue-700"
                      } text-white px-3 py-1 rounded transition`}
                    >
                      {submission.evaluated ? "Edit Review" : "Evaluate"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Evaluation Modal */}
      {selectedSubmission && (
        <div className="mt-6 p-4 border rounded bg-gray-50 dark:bg-gray-800">
          <h3 className="text-lg font-semibold mb-2">
            {selectedSubmission.evaluated ? "Edit" : "Add"} Evaluation
          </h3>
          <p className="mb-2">
            <strong>User:</strong> {selectedSubmission.userId?.name} (
            {selectedSubmission.userId?.email})
          </p>

          <label className="block mb-2">
            <span className="font-medium">Marks:</span>
            <input
              type="number"
              value={marks}
              onChange={(e) => setMarks(e.target.value)}
              placeholder="Enter marks"
              className="w-full p-2 border rounded mt-1"
              min="0"
            />
          </label>

          <label className="block mb-4">
            <span className="font-medium">Feedback/Comments:</span>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Provide detailed feedback for the student..."
              className="w-full p-2 border rounded mt-1"
              rows={4}
            />
          </label>

          <div className="flex gap-2">
            <button
              onClick={handleEvaluate}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded disabled:bg-gray-400"
            >
              {loading ? "Submitting..." : "Submit Review"}
            </button>
            <button
              onClick={() => {
                setSelectedSubmission(null);
                setComment("");
                setMarks("");
              }}
              className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainsSubmissionViewer;
